import { AlertCircle, CheckCircle2, AlertTriangle, FileText, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

interface AntecedentBasisDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  addToResources?: (resource: any) => void;
  showHeader?: boolean;
}

export default function AntecedentBasisDisplay({
  result,
  theme = 'light',
  showToast,
  addToResources,
  showHeader = true
}: AntecedentBasisDisplayProps) {
  const [expandedClaims, setExpandedClaims] = useState<Set<string>>(new Set());

  // Check if this result contains antecedent basis data
  if (!result || typeof result !== 'object') return null;

  const isAntecedentBasis = result.analysis_id !== undefined ||
                            result.status !== undefined ||
                            result.download_url !== undefined ||
                            result.full_result !== undefined;

  if (!isAntecedentBasis) return null;

  // Get the full result data
  const fullResult = result.full_result || result;
  const overallAssessment = fullResult.overall_assessment || {};
  const claimAnalysis = fullResult.claim_analysis || [];
  const crossClaimIssues = fullResult.cross_claim_issues || [];
  const recommendations = fullResult.recommendations || [];

  const toggleClaimExpansion = (claimNumber: string) => {
    const newExpanded = new Set(expandedClaims);
    if (newExpanded.has(claimNumber)) {
      newExpanded.delete(claimNumber);
    } else {
      newExpanded.add(claimNumber);
    }
    setExpandedClaims(newExpanded);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'issues_found':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'critical_issues':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass':
        return 'bg-green-50 border-green-200 text-green-900';
      case 'issues_found':
        return 'bg-yellow-50 border-yellow-200 text-yellow-900';
      case 'critical_issues':
        return 'bg-red-50 border-red-200 text-red-900';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-900';
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <Badge variant="destructive" className="text-xs">Critical</Badge>;
      case 'warning':
        return <Badge variant="secondary" className="text-xs">Warning</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{severity}</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold">Antecedent Basis Analysis</h3>
        </div>
      )}

      {/* Overall Assessment */}
      {overallAssessment.status && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {getStatusIcon(overallAssessment.status)}
              Overall Assessment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className={`p-3 rounded-lg ${getStatusColor(overallAssessment.status)}`}>
              <p className="text-sm font-medium">{overallAssessment.summary}</p>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-600">Total Issues:</span>{' '}
                <span className="font-semibold">{overallAssessment.total_issues || 0}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Critical:</span>{' '}
                <span className="font-semibold text-red-600">{overallAssessment.critical_count || 0}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Warnings:</span>{' '}
                <span className="font-semibold text-yellow-600">{overallAssessment.warning_count || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Claim-by-Claim Analysis */}
      {claimAnalysis.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Claim-by-Claim Analysis ({claimAnalysis.length} claims)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {claimAnalysis.map((claim: any, idx: number) => {
              const isExpanded = expandedClaims.has(claim.claim_number);
              const hasIssues = claim.issues && claim.issues.length > 0;

              return (
                <div key={idx} className="border rounded-lg overflow-hidden">
                  {/* Claim Header */}
                  <button
                    onClick={() => toggleClaimExpansion(claim.claim_number)}
                    className="w-full bg-gray-50 hover:bg-gray-100 px-4 py-3 flex items-center justify-between transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <span className="font-medium">Claim {parseInt(claim.claim_number, 10)}</span>
                      <Badge variant="outline" className="text-xs">
                        {claim.claim_type}
                      </Badge>
                      {claim.parent_claims && claim.parent_claims.length > 0 && (
                        <span className="text-xs text-gray-500">
                          (depends on {claim.parent_claims.join(', ')})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {hasIssues ? (
                        <Badge variant="destructive" className="text-xs">
                          {claim.issues.length} {claim.issues.length === 1 ? 'Issue' : 'Issues'}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                          No Issues
                        </Badge>
                      )}
                    </div>
                  </button>

                  {/* Claim Details (expanded) */}
                  {isExpanded && (
                    <div className="p-4 space-y-3">
                      {/* Claim Text */}
                      {claim.claim_text && (
                        <div className="bg-gray-50 p-3 rounded text-sm whitespace-pre-wrap">
                          {claim.claim_text}
                        </div>
                      )}

                      {/* Issues */}
                      {hasIssues && (
                        <div className="space-y-2">
                          <h5 className="font-medium text-sm">Issues Found:</h5>
                          {claim.issues.map((issue: any, issueIdx: number) => (
                            <div key={issueIdx} className="bg-red-50 border border-red-200 p-3 rounded-lg space-y-2">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      {getSeverityBadge(issue.severity)}
                                      <span className="text-xs font-medium text-gray-600">
                                        {issue.type?.replace(/_/g, ' ')}
                                      </span>
                                    </div>
                                    <p className="text-sm font-medium text-red-900">Element: "{issue.element}"</p>
                                  </div>
                                </div>
                              </div>
                              <div className="text-sm space-y-1 ml-6">
                                <p className="text-gray-700">
                                  <span className="font-medium">Location:</span> {issue.location}
                                </p>
                                <p className="text-gray-700">
                                  <span className="font-medium">Description:</span> {issue.description}
                                </p>
                                {issue.suggestion && (
                                  <div className="bg-blue-50 border border-blue-200 p-2 rounded mt-2">
                                    <p className="text-sm text-blue-900">
                                      <span className="font-medium">Suggestion:</span> {issue.suggestion}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Introduced vs Referenced Elements */}
                      {(claim.introduced_elements || claim.referenced_elements) && (
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {claim.introduced_elements && claim.introduced_elements.length > 0 && (
                            <div className="bg-green-50 border border-green-200 p-3 rounded">
                              <p className="font-medium text-green-900 mb-2">Introduced Elements:</p>
                              <ul className="list-disc list-inside space-y-1 text-green-800">
                                {claim.introduced_elements.map((elem: string, i: number) => (
                                  <li key={i}>{elem}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {claim.referenced_elements && claim.referenced_elements.length > 0 && (
                            <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                              <p className="font-medium text-blue-900 mb-2">Referenced Elements:</p>
                              <ul className="list-disc list-inside space-y-1 text-blue-800">
                                {claim.referenced_elements.map((elem: string, i: number) => (
                                  <li key={i}>{elem}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Cross-Claim Issues */}
      {crossClaimIssues.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              Cross-Claim Issues
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {crossClaimIssues.map((issue: any, idx: number) => (
              <div key={idx} className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg space-y-2">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-900">
                      Affects Claims: {issue.affected_claims?.join(', ')}
                    </p>
                    <p className="text-sm text-gray-700 mt-1">{issue.description}</p>
                    {issue.suggestion && (
                      <div className="bg-blue-50 border border-blue-200 p-2 rounded mt-2">
                        <p className="text-sm text-blue-900">
                          <span className="font-medium">Suggestion:</span> {issue.suggestion}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recommendations</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {recommendations.map((rec: string, idx: number) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
